import {
  Box,
  Button,
  Divider,
  List,
  Radio,
  RadioGroup,
  SystemIcon,
  SystemIconName,
  Typography,
} from '@wishket/design-system';
import { useClickOutside } from '@wishket/yogokit';

import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  MouseEvent,
  PropsWithChildren,
  useRef,
  useState,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { RecommendRoles, RoleCategory, RoleItem, Roles } from '@/shared/types';

interface SelectBoxProps extends ComponentPropsWithoutRef<'div'> {
  placeholder?: string;
  isListOpen: boolean;
  value?: string;
  leadingIcon?: SystemIconName;
  disabled?: boolean;
  error?: boolean;
}

const SelectBox = ({
  placeholder,
  value = '',
  isListOpen,
  disabled = false,
  error,
  className,
  leadingIcon,
  ...rest
}: SelectBoxProps) => {
  const generateContainerBorderStyles = () => {
    if (disabled)
      return 'border-w-gray-200 bg-w-gray-10 hover:border-w-gray-200';
    if (error) return 'border-w-red-500 hover:border-w-red-500';
    if (isListOpen) return 'border-primary-500';

    return 'border-w-gray-200 hover:border-primary-500';
  };
  const generateSelectBoxTextStyles = () => {
    if (disabled && value) return 'text-w-gray-300';
    if (disabled && !value) return 'text-w-gray-200';
    return value ? 'text-w-gray-600' : 'text-w-gray-300';
  };

  return (
    <Box
      data-testid="design-system-selectBox"
      className={twMerge(
        'flex h-[50px] w-full cursor-pointer select-none items-center justify-between rounded-xl border bg-w-white px-4 py-2.5 ',
        generateContainerBorderStyles(),
        disabled && 'cursor-not-allowed bg-w-gray-10',
        className
      )}
      {...rest}
    >
      <Box className="flex items-center gap-2">
        {leadingIcon && <SystemIcon name={leadingIcon} />}

        <Typography variant="body1" className={generateSelectBoxTextStyles()}>
          {value ? value : placeholder}
        </Typography>
      </Box>
      <div
        className={twJoin(
          'transition-transform duration-300',
          isListOpen && 'rotate-180'
        )}
      >
        <SystemIcon
          name="medium_arrow_down"
          className={disabled ? 'text-w-gray-300' : 'text-w-gray-600'}
        />
      </div>
    </Box>
  );
};

interface RadioListItemProps {
  name?: string;
  value?: string;
  label?: string;
  isSelected?: boolean;
  selectedValue?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const RadioListItem = ({
  name,
  value,
  label,
  selectedValue,
  onChange,
}: RadioListItemProps) => {
  return (
    <label key={value} className="group flex cursor-pointer gap-2">
      <Box className="mt-1">
        <Radio
          isSelected={selectedValue === value}
          name={name}
          value={value}
          onChange={onChange}
        />
      </Box>
      <Typography variant="body1">{label}</Typography>
    </label>
  );
};

interface RecommendRadioListProps {
  items: RecommendRoles;
  selectedField: RoleItem;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const RecommendRadioList = ({
  items,
  selectedField,
  onChange,
}: RecommendRadioListProps) => {
  const data = Object.keys(items.data).map((key) => ({
    key: String(key),
    value: String(key),
    label: String(items.data[key]),
  }));

  return (
    <Box className="flex flex-col gap-4">
      <Typography key={items.key} variant="body1" className="text-primary">
        {items.kor}
      </Typography>
      <RadioGroup
        name="role"
        data={data}
        selectedValue={String(selectedField.key)}
        onChange={onChange}
      >
        {(item) => (
          <RadioListItem
            key={item.key}
            name="role"
            value={item.value}
            label={item.label}
            selectedValue={String(selectedField.key)}
            onChange={onChange}
          />
        )}
      </RadioGroup>
    </Box>
  );
};

interface RadioTableProps {
  categories: RoleCategory[];
  roleItems: Roles;
  recommendItems: RecommendRoles[];
  selectedRole: RoleItem;
  closeList: () => void;
  onChange: (item: RoleItem) => void;
}

const RadioTable = ({
  categories,
  roleItems,
  recommendItems,
  selectedRole,
  closeList,
  onChange,
}: RadioTableProps) => {
  const [selectedCategory, setSelectedCategory] = useState('recommend');
  const [selectedField, setSelectedField] = useState(selectedRole);

  const isRecommend = selectedCategory === 'recommend';

  const filteredFields = !isRecommend
    ? Object.keys(roleItems[selectedCategory]).map((key) => ({
        key: key,
        value: key,
        label: roleItems[selectedCategory][key],
      }))
    : undefined;

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (isRecommend) {
      recommendItems?.forEach((item) => {
        if (item.data[value]) {
          setSelectedField({
            key: value,
            value: item.data[value],
          });
        }
      });
    } else {
      setSelectedField({
        key: value,
        value: roleItems[selectedCategory][value],
      });
    }
  };

  return (
    <>
      <Box className="p-6">
        <Box className="flex h-[268px] rounded-xl border">
          <Box className="w-fit shrink-0 p-2 desktop:w-[140px]">
            {recommendItems && (
              <Box
                key="recommend"
                className={twJoin(
                  'flex cursor-pointer items-center gap-2 rounded-xl px-4 py-3 hover:bg-primary-10',
                  selectedCategory === 'recommend' && 'bg-primary-10'
                )}
                onClick={() => setSelectedCategory('recommend')}
              >
                <Typography
                  variant="body1"
                  className={twMerge(
                    'hover text-w-gray-600',
                    selectedCategory === 'recommend' && 'text-primary-500'
                  )}
                >
                  추천
                </Typography>
              </Box>
            )}
            {categories.map((category) => (
              <Box
                key={category.key}
                className={twJoin(
                  'flex cursor-pointer items-center gap-2 rounded-xl px-4 py-3 hover:bg-primary-10',
                  selectedCategory === category.key && 'bg-primary-10'
                )}
                onClick={() => setSelectedCategory(category.key)}
              >
                <Typography
                  variant="body1"
                  className={twMerge(
                    'text-w-gray-600',
                    selectedCategory === category.key && 'text-primary-500'
                  )}
                >
                  {category.kor}
                </Typography>
              </Box>
            ))}
          </Box>
          <Divider isVertical />
          <Box className="flex h-full w-full flex-col gap-3.5 overflow-x-hidden overflow-y-scroll py-4 pl-6 pr-2 scrollbar-list">
            {isRecommend ? (
              recommendItems?.map((item) => (
                <RecommendRadioList
                  key={item.key}
                  items={item}
                  selectedField={selectedField}
                  onChange={handleFieldChange}
                />
              ))
            ) : (
              <RadioGroup
                name="role"
                data={filteredFields || []}
                selectedValue={String(selectedField.key)}
                onChange={handleFieldChange}
              >
                {(item) => (
                  <RadioListItem
                    key={item.key}
                    name="role"
                    value={item.value}
                    label={item.label}
                    selectedValue={String(selectedField.key)}
                    onChange={handleFieldChange}
                  />
                )}
              </RadioGroup>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="flex justify-end gap-4 border-t px-6 py-5">
        <Button className="w-[90px] text-w-gray-600" onClick={closeList}>
          취소
        </Button>
        <Button
          className="w-[90px]"
          variant="solid"
          onClick={() => onChange(selectedField)}
        >
          선택하기
        </Button>
      </Box>
    </>
  );
};

interface RoleSelectProps {
  categories: RoleCategory[];
  roleItems?: Roles;
  recommendItems?: RecommendRoles[];
  placeholder?: string;
  handleChange: (item: RoleItem) => void;
  selectedItem: RoleItem;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

// TODO: WDS에 해당 컴포넌트 추가될 때 변경 필요
export const RoleSelect = ({
  categories,
  roleItems,
  recommendItems,
  selectedItem,
  handleChange,
  placeholder,
  disabled,
  error,
}: RoleSelectProps) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const closeList = () => {
    if (!isListOpen) return;

    setIsListOpen(false);
  };

  const handleItemClick = (item: RoleItem) => {
    handleChange({ key: item.key, value: item.value });
    closeList();
  };

  const toggleListOpen = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    setIsListOpen(!isListOpen);
  };

  useClickOutside(listRef, closeList);

  return (
    <Box className="relative w-full" ref={listRef}>
      <SelectBox
        placeholder={placeholder}
        value={selectedItem?.value}
        disabled={disabled}
        isListOpen={isListOpen}
        onClick={toggleListOpen}
        error={error}
      />
      {isListOpen && (
        <Box className="absolute top-[54px] z-10 w-full">
          <List.Root className="max-h-[420px] w-full p-0">
            {roleItems && (
              <RadioTable
                categories={categories}
                roleItems={roleItems}
                recommendItems={recommendItems || []}
                selectedRole={selectedItem}
                closeList={closeList}
                onChange={handleItemClick}
              />
            )}
          </List.Root>
        </Box>
      )}
    </Box>
  );
};

interface LabeledContentProps {
  id?: string;
  title: string;
  isError?: boolean;
  helperText?: string;
  onFocus?: () => void;
  onClick?: () => void;
}

export const LabeledContent = ({
  id,
  title,
  isError,
  children,
  helperText,
  onClick,
  onFocus,
}: PropsWithChildren<LabeledContentProps>) => {
  return (
    <Box
      className="flex flex-col gap-4"
      id={id}
      onFocus={onFocus}
      onClick={onClick}
    >
      <Box className="flex gap-1">
        <Typography variant="body1" className="font-medium text-w-gray-900">
          {title}
        </Typography>
        <Typography variant="body1" className="font-medium text-primary">
          *
        </Typography>
      </Box>
      <Box className="flex w-full flex-col gap-3">
        {children}
        {helperText && (
          <Typography
            variant="body2"
            className={twMerge(
              'ml-4 text-w-gray-400',
              isError && 'text-w-red-500'
            )}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
